import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { BREAKPOINTS, type TileItem } from "@csbi/contracts"
import {
  createDashboard,
  deleteDashboard,
  fetchDashboard,
  fetchDashboards,
  fetchHomeDashboard,
  putDashboardContent,
  updateDashboard,
} from "./api.js"
import type { Layouts } from "./grid.js"
import { isNewTile, layoutOf } from "./new-tile.js"

export function useDashboards() {
  return useQuery({
    queryKey: ["dashboards"],
    queryFn: ({ signal }) => fetchDashboards(signal),
    staleTime: 5 * 60_000,
  })
}

export function useDashboard(idOrSlug: string) {
  return useQuery({
    queryKey: ["dashboard", idOrSlug],
    queryFn: ({ signal }) => fetchDashboard(idOrSlug, signal),
    enabled: idOrSlug.length > 0,
    staleTime: 5 * 60_000,
  })
}

export function useSaveDashboardEdits(idOrSlug: string) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: ({
      name,
      description,
      tiles,
      layouts,
    }: {
      name: string
      description: string | null
      tiles: readonly TileItem[]
      layouts: Layouts
    }) =>
      putDashboardContent(idOrSlug, {
        name,
        description,
        tiles: tiles.map((t) => ({
          ...(isNewTile(t.id) ? {} : { id: t.id }),
          title: t.title,
          config: t.config,
          layout: layoutOf(layouts, t.id, BREAKPOINTS),
        })),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["dashboard", idOrSlug] })
      void qc.invalidateQueries({ queryKey: ["dashboard", "home"] })
      void qc.invalidateQueries({ queryKey: ["dashboards"] })
    },
  })
}

export function useCreateDashboard() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: createDashboard,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dashboards"] }),
  })
}

export function useDeleteDashboard() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: deleteDashboard,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["dashboards"] })
      void qc.invalidateQueries({ queryKey: ["dashboard", "home"] })
    },
  })
}

export function useHomeDashboard() {
  return useQuery({
    queryKey: ["dashboard", "home"],
    queryFn: ({ signal }) => fetchHomeDashboard(signal),
    staleTime: 5 * 60_000,
  })
}

export function useUpdateDashboard(idOrSlug: string) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (body: Parameters<typeof updateDashboard>[1]) => updateDashboard(idOrSlug, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["dashboard", idOrSlug] })
      void qc.invalidateQueries({ queryKey: ["dashboard", "home"] })
      void qc.invalidateQueries({ queryKey: ["dashboards"] })
    },
  })
}
